import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, Edit2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import VintageLayout from '../components/layout/VintageLayout';

interface Template {
    _id: string;
    text: string;
    createdAt: string;
}

interface Pack {
    _id: string;
    name: string;
    description: string;
    isAdult: boolean;
}

export const PackEditorPage: React.FC = () => {
    const { packId } = useParams<{ packId: string }>();
    const navigate = useNavigate();
    const [pack, setPack] = useState<Pack | null>(null);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [newCardText, setNewCardText] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');

    useEffect(() => {
        fetchPackDetails();
    }, [packId]);

    const fetchPackDetails = async () => {
        try {
            setLoading(true);
            const [packRes, templatesRes] = await Promise.all([
                api.get(`/packs/${packId}`),
                api.get(`/packs/${packId}/templates`)
            ]);
            setPack(packRes.data);
            setTemplates(templatesRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement du pack');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCard = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCardText.trim()) return;

        try {
            const res = await api.post(`/packs/${packId}/templates`, { text: newCardText });
            setTemplates([res.data, ...templates]);
            setNewCardText('');
            toast.success('Carte ajoutée !');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout');
        }
    };

    const handleDeleteCard = async (id: string) => {
        if (!window.confirm('Supprimer cette carte ?')) return;
        try {
            await api.delete(`/packs/templates/${id}`);
            setTemplates(templates.filter(t => t._id !== id));
            toast.success('Carte supprimée');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const startEditing = (template: Template) => {
        setEditingId(template._id);
        setEditText(template.text);
    };

    const saveEdit = async () => {
        if (!editingId || !editText.trim()) return;
        try {
            const res = await api.put(`/packs/templates/${editingId}`, { text: editText });
            setTemplates(templates.map(t => t._id === editingId ? res.data : t));
            setEditingId(null);
            toast.success('Carte modifiée');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erreur lors de la modification');
        }
    };

    if (loading) return <div className="min-h-screen bg-navy-950 flex items-center justify-center text-gold-400">Chargement...</div>;

    return (
        <VintageLayout>
            <div className="min-h-screen text-cream-100 font-sans p-8">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="p-2 rounded-full border border-gold-500/30 text-gold-400 hover:bg-gold-500/10 transition-colors"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <h1 className="text-3xl font-serif font-bold text-gold-100">{pack?.name}</h1>
                            <p className="text-cream-200/60">{pack?.description}</p>
                        </div>
                        <div className="ml-auto bg-navy-900/50 px-4 py-2 rounded-lg border border-gold-500/20">
                            <span className="text-gold-400 font-bold">{templates.length}</span> cartes
                        </div>
                    </div>

                    {/* Add Card Form */}
                    <div className="bg-navy-900/60 backdrop-blur-md border border-gold-500/30 rounded-xl p-6 mb-8 shadow-lg">
                        <h2 className="text-xl font-serif font-bold text-gold-400 mb-4 flex items-center gap-2">
                            <Plus size={20} /> Ajouter une carte
                        </h2>
                        <form onSubmit={handleAddCard} className="flex gap-4">
                            <input
                                type="text"
                                value={newCardText}
                                onChange={(e) => setNewCardText(e.target.value)}
                                placeholder="Écrivez un souvenir ou une situation..."
                                className="flex-1 bg-navy-950 border border-navy-700 rounded-lg px-4 py-3 text-cream-100 focus:border-gold-500 outline-none"
                            />
                            <button
                                type="submit"
                                disabled={!newCardText.trim()}
                                className="btn-vintage px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Ajouter
                            </button>
                        </form>
                    </div>

                    {/* Cards List */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {templates.map(template => (
                            <div key={template._id} className="bg-navy-800/50 border border-gold-500/10 rounded-xl p-4 hover:border-gold-500/30 transition-all group">
                                {editingId === template._id ? (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            className="flex-1 bg-navy-950 border border-gold-500/50 rounded px-2 py-1 text-cream-100 outline-none"
                                            autoFocus
                                        />
                                        <button onClick={saveEdit} className="text-green-400 hover:text-green-300"><Save size={20} /></button>
                                        <button onClick={() => setEditingId(null)} className="text-red-400 hover:text-red-300">Annuler</button>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-start gap-4">
                                        <p className="text-lg font-serif text-cream-100">{template.text}</p>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => startEditing(template)}
                                                className="text-gold-400 hover:text-gold-300 p-1"
                                                title="Modifier"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCard(template._id)}
                                                className="text-red-400 hover:text-red-300 p-1"
                                                title="Supprimer"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {templates.length === 0 && (
                            <div className="col-span-full text-center py-12 text-cream-200/30 italic">
                                Ce pack est vide. Ajoutez votre première carte !
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </VintageLayout>
    );
};
